// Welcome Video Upload Component
// Handles video upload, thumbnail, and preview

import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Loader2, Video, Image as ImageIcon, X } from 'lucide-react';

const WelcomeVideoUpload = ({ 
  videoUrl, 
  thumbnailUrl,
  uploadingVideo, 
  uploadingThumbnail,
  onVideoUpload,
  onThumbnailUpload,
  onRemoveVideo
}) => {
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Video className="w-5 h-5 text-teal-500" />
          Welcome Video
          <span className="text-xs font-normal text-slate-400 ml-2">(Optional)</span>
        </CardTitle>
        <CardDescription>
          Add a video greeting that plays when visitors open the chat widget
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {videoUrl ? (
            <div className="space-y-3">
              {/* Video Preview */}
              <div className="relative aspect-video bg-slate-900 rounded-xl overflow-hidden">
                <video 
                  src={`${process.env.REACT_APP_BACKEND_URL}${videoUrl}`}
                  className="w-full h-full object-contain"
                  controls
                  poster={thumbnailUrl ? `${process.env.REACT_APP_BACKEND_URL}${thumbnailUrl}` : undefined}
                />
              </div>
              
              {/* Thumbnail Upload */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-1">Custom Thumbnail</p>
                  {thumbnailUrl ? (
                    <div className="relative w-24 h-14 rounded overflow-hidden border">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${thumbnailUrl}`}
                        alt="Thumbnail" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">No custom thumbnail</p>
                  )}
                </div>
                <input
                  type="file"
                  ref={thumbnailInputRef}
                  onChange={onThumbnailUpload}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => thumbnailInputRef.current?.click()}
                  disabled={uploadingThumbnail}
                  data-testid="upload-thumbnail-btn"
                >
                  {uploadingThumbnail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {/* Remove Video */}
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={onRemoveVideo}
                data-testid="remove-video-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Remove Video
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="aspect-video bg-slate-100 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-slate-300">
                <Video className="w-10 h-10 text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">No video uploaded</p>
                <p className="text-xs text-slate-400">MP4, WebM, MOV (max 50MB)</p>
              </div>
              
              <input
                type="file"
                ref={videoInputRef}
                onChange={onVideoUpload}
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
              />
              
              <Button
                variant="outline"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
                data-testid="upload-video-btn"
              >
                {uploadingVideo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Video
                  </>
                )}
              </Button>
            </div>
          )}
          
          <p className="text-xs text-slate-500">
            Visitors will see a thumbnail with a play button. The video plays when they tap it, 
            then the intake questions begin.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WelcomeVideoUpload;
